'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { BarcodeScanner, BarcodeFormat } from '@capacitor-mlkit/barcode-scanning';

import { Capacitor } from '@capacitor/core';
import ManagedConfigurations from './utils/managed-config';
import BrandedModal from '../components/BrandedModal';

export default function Home() {
  const router = useRouter();
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [unitCode, setUnitCode] = useState<string | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    type: 'warning' | 'success' | 'error';
    title: string;
    message: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    type: 'error',
    title: '',
    message: ''
  });

  const [lastScanned, setLastScanned] = useState<string | null>(null);

  // Activity Timer Logic
  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
  };

  const stopScan = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      await BarcodeScanner.stopScan();
    }
    setIsScanning(false);
    clearInactivityTimer();
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      stopScan();
    }, 30000); // 30 seconds
  }, [stopScan]);

  // Load Last Scanned from Storage
  useEffect(() => {
    const cached = localStorage.getItem('lastLoyaltyScan');
    if (cached) {
      try {
        const { code, timestamp } = JSON.parse(cached);
        const now = Date.now();
        const elapsed = now - timestamp;
        const THREE_MINUTES = 3 * 60 * 1000;

        if (elapsed < THREE_MINUTES) {
          setLastScanned(code);

          // Set timer for the remaining time
          const remaining = THREE_MINUTES - elapsed;
          setTimeout(() => {
            setLastScanned(null);
            localStorage.removeItem('lastLoyaltyScan');
          }, remaining);
        } else {
          // Already expired
          localStorage.removeItem('lastLoyaltyScan');
        }
      } catch (e) {
        // Fallback or bad format - clear it
        localStorage.removeItem('lastLoyaltyScan');
      }
    }
  }, []);

  const startScan = useCallback(async () => {
    const isNative = Capacitor.isNativePlatform();

    if (isNative) {
      try {
        await BarcodeScanner.requestPermissions();
        await BarcodeScanner.startScan({ formats: [BarcodeFormat.QrCode] });
      } catch (error) {
        console.error('Scan error:', error);
        setIsScanning(false);
        return;
      }
    }

    setIsScanning(true);
    setScanResult(null);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleManualStop = () => {
    stopScan();
  };

  // Manage Background Transparency
  useEffect(() => {
    if (isScanning && Capacitor.isNativePlatform()) {
      document.body.style.background = 'transparent';
      document.documentElement.style.background = 'transparent';
    } else {
      document.body.style.background = '';
      document.documentElement.style.background = '';
    }
  }, [isScanning]);

  const handleLoyaltyScan = async (code: string) => {
    await stopScan();

    // Cache valid scan with timestamp
    const cacheData = JSON.stringify({ code, timestamp: Date.now() });
    localStorage.setItem('lastLoyaltyScan', cacheData);
    setLastScanned(code);

    // Set auto-clear timer for THIS specific new scan
    setTimeout(() => {
      setLastScanned(prev => {
        // Only clear if it matches the current code (check storage logic)
        const current = localStorage.getItem('lastLoyaltyScan');
        if (current === cacheData) {
          localStorage.removeItem('lastLoyaltyScan');
          return null;
        }
        return prev;
      });
    }, 3 * 60 * 1000); // 3 minutes

    // Show loading state if needed, or just proceed
    // Ideally we would show a loading spinner

    try {
      const response = await fetch('/api/member-details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loyaltyId: code,
          unitCode: unitCode
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'ok' && data.membership) {
        const { firstName, lastName, tags } = data.membership;
        const type = tags && tags.includes('STAFF') ? 'STAFF' : 'CUSTOMER';
        // Handle case where lastName might be empty
        const lastInitial = lastName ? lastName.charAt(0) : '';
        const name = `${firstName} ${lastInitial}`;
        const tokenQuery = data.qrToken ? `&qrToken=${encodeURIComponent(data.qrToken)}` : '';

        router.push(`/transaction?loyaltyId=${encodeURIComponent(data.membership.commonExtId || code)}&name=${encodeURIComponent(name)}&type=${encodeURIComponent(type)}${tokenQuery}`);
      } else {
        // Handle "Customer not found" or other API errors
        const errorMessage = data.errors && data.errors.length > 0 ? data.errors[0].message : 'Invalid Loyalty ID';

        // Clear invalid scan from storage/state
        localStorage.removeItem('lastLoyaltyScan');
        setLastScanned(null);

        setModalConfig({
          isOpen: true,
          type: 'error',
          title: 'Invalid Loyalty ID',
          message: errorMessage,
          onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
          cancelText: 'Close'
        });
      }
    } catch (e) {
      console.error(e);
      // Clear invalid scan from storage/state
      localStorage.removeItem('lastLoyaltyScan');
      setLastScanned(null);

      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'System Error',
        message: 'An error occurred while validating the loyalty card.',
        onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false })),
        cancelText: 'Close'
      });
    }
  };

  // Listener for scan results
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let scanListener: any;

    if (isScanning) {
      const setupListener = async () => {
        scanListener = await BarcodeScanner.addListener('barcodesScanned', async (result) => {
          if (result.barcodes.length > 0) {
            const code = result.barcodes[0].rawValue;
            if (code) {
              handleLoyaltyScan(code);
            }
          }
        });
      };
      setupListener();
    }

    return () => {
      if (scanListener) {
        scanListener.remove();
      }
    };
  }, [isScanning, stopScan]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopScan();
    };
  }, [stopScan]);

  useEffect(() => {
    const fetchUnitCode = async () => {
      try {
        // 1. Check Cache First
        const cached = localStorage.getItem('unit_code');
        if (cached) {
          setUnitCode(cached);
        }

        // 2. Fetch from Plugin
        const result = await ManagedConfigurations.getUnitCode();
        const mdmCode = result.value;

        // Override cache ONLY if:
        // 1. MDM code is not "Unknown_Unit", "NoCache", or Null/Empty
        // 2. MDM code is different from what we have cached
        if (mdmCode &&
          mdmCode !== 'Unknown_Unit' &&
          mdmCode !== 'NoCache' &&
          mdmCode !== cached) {
          setUnitCode(mdmCode);
          localStorage.setItem('unit_code', mdmCode);
        } else if (!cached && (mdmCode === 'Unknown_Unit' || !mdmCode)) {
          // No cache and unknown unit -> NoCache
          setUnitCode('NoCache');
        }
      } catch (e) {
        console.error('Failed to fetch unit code', e);
        const cached = localStorage.getItem('unit_code');
        if (!cached) setUnitCode('NoCache');
      }
    };

    fetchUnitCode();

    // Listen for live updates
    let listener: { remove: () => void } | undefined;
    const setupListener = async () => {
      try {
        listener = await ManagedConfigurations.addListener('restrictionsChanged', (data) => {
          if (data.value !== 'Unknown_Unit') {
            setUnitCode(data.value);
            localStorage.setItem('unit_code', data.value);
          } else {
            // If it changes back to unknown, we only show NoCache if we have nothing else
            setUnitCode(prev => prev || 'NoCache');
          }
        });
      } catch (e) {
        console.error('Failed to add restrictions listener', e);
      }
    };
    setupListener();

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, []);

  const handleTap = () => {
    if (!isScanning) {
      startScan();
    }
  };

  return (
    <main
      onClick={handleTap}
      className={`min-h-screen flex flex-col items-center justify-center p-6 transition-colors duration-500 relative overflow-hidden cursor-pointer ${isScanning ? 'bg-transparent' : 'bg-white dark:bg-neutral-900'}`}
    >
      <BrandedModal {...modalConfig} />

      {/* Unit Code Display */}
      {unitCode && (
        <div className="absolute top-8 left-4 z-50 bg-black/20 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/10">
          <p className="text-xs text-white/70 font-medium uppercase tracking-wider">Unit Code</p>
          <p className="text-white font-mono font-bold text-shadow-sm">{unitCode}</p>
        </div>
      )}

      {/* Background Mask for visual focus during scan */}
      {isScanning && (
        <div className="fixed inset-0 z-10 pointer-events-none">
          {/* This creates a dark overlay everywhere EXCEPT the center box using a massive box-shadow */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[70vw] max-w-[300px] max-h-[300px] rounded-3xl border-2 border-white/50"
            style={{ boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)' }}
          >
            {/* Optional: Scanning animation line */}
            <div className="w-full h-1 bg-blue-500/50 absolute top-0 animate-[scan_2s_ease-in-out_infinite]" />
          </div>
        </div>
      )}

      {/* Logo - Moves to top during scan. Direct child of main for better control. */}
      <div
        className={`transition-all duration-700 ease-in-out z-50 ${isScanning
          ? 'fixed top-8 w-32 h-16 opacity-100'
          : 'relative w-64 h-32 mb-8 opacity-100'
          }`}
      >
        <Image
          src="/egotm-stacked.svg"
          alt="Logo"
          fill
          className="object-contain"
          priority
        />
      </div>

      {/* Home State: Tap Text */}
      <div className={`fixed bottom-20 z-20 transition-all duration-500 transform pointer-events-none ${isScanning ? 'opacity-0 translate-y-20' : 'opacity-100 translate-y-0'}`}>
        <p className="text-xl font-medium text-neutral-500 dark:text-neutral-400 animate-pulse">
          Tap To Scan Loyalty
        </p>
      </div>

      {/* Scanning State: Instructions / Feedback - Moved further down */}
      <div className={`fixed bottom-12 z-30 transition-all duration-500 ${isScanning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
        <div className="flex flex-col items-center gap-4">
          <p className="text-white text-lg font-medium drop-shadow-md text-center px-4">Align QR code within the frame</p>

          <div className="flex items-center gap-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleManualStop();
              }}
              className="px-8 py-3 bg-red-500/80 backdrop-blur-md border border-white/20 text-white rounded-full text-base font-bold shadow-lg hover:bg-red-600/90 transition-all active:scale-95"
            >
              Cancel
            </button>

            {lastScanned && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleLoyaltyScan(lastScanned);
                }}
                className="px-8 py-3 bg-eg-green-pantone-368c/90 backdrop-blur-md border border-white/20 text-white rounded-full text-base font-bold shadow-lg hover:bg-eg-green-pantone-368c transition-all active:scale-95"
              >
                Use Last
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Result Display (Optional demo output) */}
      {scanResult && !isScanning && (
        <div className="fixed bottom-10 z-20 animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-green-100 border border-green-200 text-green-800 px-6 py-4 rounded-xl shadow-lg flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            <div>
              <p className="text-sm font-bold uppercase tracking-wider opacity-75">Loyalty ID Scanned</p>
              <p className="font-mono text-xl">{scanResult}</p>
            </div>
          </div>
        </div>
      )}

      <style jsx global>{`
        @keyframes scan {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </main>
  );
}
